// client/src/context/NotificationContext.js
import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import io from 'socket.io-client';
import { AuthContext } from './AuthContext';
import { API_BASE_URL } from '../utils/media';

export const NotificationContext = createContext({ notifications: [], unreadCount: 0, refresh: () => {}, markRead: () => {}, markAllRead: () => {} });

export const NotificationProvider = ({ children }) => {
  const { token, user } = useContext(AuthContext);
  const [notifications, setNotifications] = useState([]);
  const socketRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) setNotifications(await response.json());
    } catch {
      // Non-critical — the bell just stays at whatever it last had.
    }
  }, [token]);

  useEffect(() => { refresh(); }, [refresh]);

  // Live updates: the server emits a generic 'notification' event with the
  // target userId whenever one is created (order updates, messages...).
  useEffect(() => {
    if (!token || !user) return;
    // undefined (not '') tells socket.io-client to connect to the page's own
    // origin, which is what we want once API_BASE_URL is relative/empty.
    const socket = io(API_BASE_URL || undefined);
    socketRef.current = socket;
    socket.on('notification', ({ userId }) => {
      if (userId === user.id || userId === user.userId) refresh();
    });
    return () => socket.disconnect();
  }, [token, user, refresh]);

  const markRead = async (id) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
    try {
      await fetch(`${API_BASE_URL}/api/notifications/${id}/read`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {
      // Best-effort — a failed mark-as-read isn't worth surfacing to the user.
    }
  };

  const markAllRead = async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    try {
      await fetch(`${API_BASE_URL}/api/notifications/read-all`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {
      // Best-effort.
    }
  };

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  return (
    <NotificationContext.Provider value={{ notifications, unreadCount, refresh, markRead, markAllRead }}>
      {children}
    </NotificationContext.Provider>
  );
};
